import React, { useCallback, useEffect, useMemo, useState } from 'react'
import FiltersPanel from '../components/FiltersPanel'
import SelectedEventsPanel from '../components/SelectedEventsPanel'
import SelectionToolbar from '../components/SelectionToolbar'
import { defaultEventFilters, mockLeagueNewEvents } from '../constants/mockLeagueEvents'
import useEventFilters from '../hooks/useEventFilters'
import LeagueTable from '../components/LeagueTable'
import LeagueEventsTable from '../components/LeagueEventsTable'
import leagueSelectionService from '../../../services/leagueSelectionService'
import { LoadingSpinner } from '../../../components'
import { useWindowWidth } from '@react-hook/window-size';
import { useDispatch } from 'react-redux'
import { setToastMessage } from '../../../reducers/common.reducer'
import IFramePanel from '../components/IFramePanel'
import { useLogTrail } from '../../../hooks/useLogTrail'
import moment from 'moment-timezone'

const LEAGUES_PER_PAGE = 8
const EVENTS_PER_PAGE = 10
const DEFAULT_REGION = 'N/A'
const DEFAULT_MARKET = 'N/A'

const normaliseLeagueEvents = (rawLeagues = []) => {
  if (!Array.isArray(rawLeagues)) {
    return []
  }

  return rawLeagues
    .flatMap((league) => {
      if (!league) return []
      const {
        leagueId,
        rapidLeagueId,
        leagueName,
        region,
        country,
        countryName,
        eventDetails,
      } = league

      const safeLeagueId = leagueId ?? rapidLeagueId ?? ''
      const safeRegion = region ?? country ?? countryName ?? DEFAULT_REGION
      const details = Array.isArray(eventDetails) ? eventDetails : []

      return details.map((event, index) => {
        if (!event) return null

        const {
          upcommingEventId,
          rapidEventId,
          homeTeam,
          awayTeam,
          eventTime,
          eventDate,
          market,
          isMix,
          isSingle,
          isFivePercent,
          isEightPercent,
        } = event

        const eventIdentifier = upcommingEventId ?? rapidEventId ?? `${safeLeagueId}-${index}`

        return {
          id: eventIdentifier ? String(eventIdentifier) : `${safeLeagueId}-${index}`,
          leagueId: String(safeLeagueId ?? ''),
          leagueName: leagueName ?? 'Unknown league',
          region: safeRegion || DEFAULT_REGION,
          homeTeam: homeTeam ?? 'N/A',
          homeTeamId: event.homeTeamId ?? null,
          awayTeamId: event.awayTeamId ?? null, 
          awayTeam: awayTeam ?? 'N/A',
          eventDate: eventDate ?? '',
          eventTime: eventTime ?? eventDate ?? '',
          market: market ?? DEFAULT_MARKET,
          rapidLeagueId: rapidLeagueId ?? null,
          rapidEventId: rapidEventId ?? null,
          upcommingEventId: upcommingEventId ?? null,
          isMix: Boolean(isMix),
          isSingle: Boolean(isSingle),
          isFivePercent: Boolean(isFivePercent),
          isEightPercent: Boolean(isEightPercent),
        }
      })
    })
    .filter(Boolean)
}

const buildInitialSelections = (events) =>
  events.reduce((accumulator, event) => {
    if (!event?.id) return accumulator

    const selection = {
      id: event.id,
      parlay: Boolean(event.isMix),
      single: Boolean(event.isSingle),
      fivePercent: Boolean(event.isSingle && event.isFivePercent),
      eightPercent: Boolean(event.isSingle && event.isEightPercent),
    }

    if (!selection.parlay && !selection.single) {
      return accumulator
    }

    accumulator[event.id] = selection
    return accumulator
  }, {})

const LeagueSelectionPage = () => {
  const { logs, fetchLogs, saveLog } = useLogTrail({
    selectedDate: moment(new Date()).format("YYYY-MM-DD"),
    title: 'League Selection',
  });
  const dispatch = useDispatch();
  const [events, setEvents] = useState([])
  const onlyWidth = useWindowWidth();
  const [selectionState, setSelectionState] = useState({})
  const [lastSavedAt, setLastSavedAt] = useState(null)
  const [activeLeagueId, setActiveLeagueId] = useState('')
  const [leaguePage, setLeaguePage] = useState(1)
  const [leagueSortOption, setLeagueSortOption] = useState('nameAsc')
  const [eventPage, setEventPage] = useState(1)
  const [isLoading, setIsLoading] = useState(false)
  const [fetchError, setFetchError] = useState('')
  const { filters, updateFilters, resetFilters, filteredEvents } = useEventFilters(events, defaultEventFilters);
  const [leagueSearchTerm, setLeagueSearchTerm] = useState('')
  const [filtersVisible, setFiltersVisible] = useState(false)
  const [referenceVisible, setReferenceVisible] = useState(false);

  const selectedEvents = useMemo(
    () =>
      Object.values(selectionState).map((value) => {
        const baseEvent = events.find((event) => event.id === value.id) || {}
        return {
          ...baseEvent,
          ...value,
        }
      }),
    [events, selectionState],
  )

  const selectedCount = selectedEvents.length
  const leagues = useMemo(() => {
    const leagueMap = new Map()
    filteredEvents.forEach((event) => {
      if (!leagueMap.has(event.leagueId)) {
        leagueMap.set(event.leagueId, {
          leagueId: event.leagueId,
          leagueName: event.leagueName,
          region: event.region,
          eventsCount: 0,
        })
      }
      const league = leagueMap.get(event.leagueId)
      league.eventsCount += 1
    })
    const leagueList = Array.from(leagueMap.values())

    const compareByNameAsc = (a, b) =>
      String(a.leagueName || '').localeCompare(String(b.leagueName || ''), undefined, {
        sensitivity: 'base',
      })
    const compareByNameDesc = (a, b) => compareByNameAsc(b, a)
    const normaliseLeagueId = (league) => {
      const numericId = Number(league.leagueId)
      return Number.isFinite(numericId) ? numericId : String(league.leagueId || '')
    }
    const compareByIdAsc = (a, b) => {
      const aId = normaliseLeagueId(a)
      const bId = normaliseLeagueId(b)
      if (typeof aId === 'number' && typeof bId === 'number') {
        return aId - bId
      }
      return String(aId).localeCompare(String(bId), undefined, { numeric: true, sensitivity: 'base' })
    }
    const compareByIdDesc = (a, b) => compareByIdAsc(b, a)

    switch (leagueSortOption) {
      case 'nameDesc':
        return leagueList.sort(compareByNameDesc)
      case 'idAsc':
        return leagueList.sort(compareByIdAsc)
      case 'idDesc':
        return leagueList.sort(compareByIdDesc)
      case 'nameAsc':
      default:
        return leagueList.sort(compareByNameAsc)
    }
  }, [filteredEvents, leagueSortOption])

  const searchableLeagues = useMemo(() => {
    const query = leagueSearchTerm.trim().toLowerCase()
    if (!query) {
      return leagues
    }
    return leagues.filter((league) => {
      const name = String(league?.leagueName ?? '').toLowerCase()
      const region = String(league?.region ?? '').toLowerCase()
      const identifier = String(league?.leagueId ?? '').toLowerCase()
      return (
        name.includes(query) ||
        region.includes(query) ||
        identifier.includes(query)
      )
    })
  }, [leagues, leagueSearchTerm])

  const totalLeaguePages = useMemo(
    () => (searchableLeagues.length ? Math.ceil(searchableLeagues.length / LEAGUES_PER_PAGE) : 0),
    [searchableLeagues],
  )

  const paginatedLeagues = useMemo(() => {
    if (!searchableLeagues.length) return []
    const start = (leaguePage - 1) * LEAGUES_PER_PAGE
    return searchableLeagues.slice(start, start + LEAGUES_PER_PAGE)
  }, [searchableLeagues, leaguePage])

  const leagueStartIndex = useMemo(() => {
    if (!searchableLeagues.length) return 0
    return (leaguePage - 1) * LEAGUES_PER_PAGE + 1
  }, [searchableLeagues.length, leaguePage])

  const leagueEndIndex = useMemo(() => {
    if (!searchableLeagues.length) return 0
    return Math.min(leagueStartIndex + paginatedLeagues.length - 1, searchableLeagues.length)
  }, [leagueStartIndex, paginatedLeagues.length, searchableLeagues.length])

  const loadLeagueEvents = useCallback(async () => {
    setIsLoading(true)
    setFetchError('')
    try {
      const response = await leagueSelectionService.getRawLeagueEvents()
      const payload = response?.data?.data ?? response?.data ?? []
      const normalisedEvents = normaliseLeagueEvents(payload)

      setEvents(normalisedEvents)
      setSelectionState(buildInitialSelections(normalisedEvents))
    } catch (error) {
      console.error('Failed to fetch league events', error)
      setFetchError('Unable to load league data right now. Showing the latest available information.')
      const fallbackEvents = normaliseLeagueEvents(mockLeagueNewEvents)
      setEvents((current) => (current.length ? current : fallbackEvents))
      setSelectionState((current) =>
        Object.keys(current).length ? current : buildInitialSelections(fallbackEvents),
      )
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    loadLeagueEvents()
    fetchLogs()
  }, [fetchLogs, loadLeagueEvents])

  useEffect(() => {
    setLeaguePage((current) => (current === 1 ? current : 1))
  }, [leagueSortOption])

  useEffect(() => {
    if (!leagues.length) {
      setActiveLeagueId('')
      if (leaguePage !== 1) {
        setLeaguePage(1)
      }
      return
    }
    if (leaguePage > totalLeaguePages) {
      setLeaguePage(totalLeaguePages)
    }
    if (!leagues.some((league) => league.leagueId === activeLeagueId)) {
      setActiveLeagueId(leagues[0].leagueId)
    }
  }, [leagues, activeLeagueId, leaguePage, totalLeaguePages])

  useEffect(() => {
    setLeaguePage(1)
  }, [leagueSearchTerm])

  const activeLeagueEvents = useMemo(
    () => filteredEvents.filter((event) => event.leagueId === activeLeagueId),
    [filteredEvents, activeLeagueId],
  )

  const allLeagueParlaySelected = useMemo(() => {
    if (!activeLeagueEvents.length) return false
    return activeLeagueEvents.every((event) => selectionState[event.id]?.parlay)
  }, [activeLeagueEvents, selectionState])

  const allLeagueSingleSelected = useMemo(() => {
    if (!activeLeagueEvents.length) return false
    return activeLeagueEvents.every((event) => selectionState[event.id]?.single)
  }, [activeLeagueEvents, selectionState])

  const allUpliftSelected = useMemo(() => {
    if (!activeLeagueEvents.length) return false
    return activeLeagueEvents.every((event) => selectionState[event.id]?.fivePercent)
  }, [activeLeagueEvents, selectionState])

  useEffect(() => {
    setEventPage(1)
  }, [activeLeagueId])

  const totalEventPages = useMemo(
    () => (activeLeagueEvents.length ? Math.ceil(activeLeagueEvents.length / EVENTS_PER_PAGE) : 0),
    [activeLeagueEvents.length],
  )

  const paginatedEvents = useMemo(() => {
    if (!activeLeagueEvents.length) return []
    const start = (eventPage - 1) * EVENTS_PER_PAGE
    return activeLeagueEvents.slice(start, start + EVENTS_PER_PAGE)
  }, [activeLeagueEvents, eventPage])

  const eventStartIndex = useMemo(() => {
    if (!activeLeagueEvents.length) return 0
    return (eventPage - 1) * EVENTS_PER_PAGE + 1
  }, [activeLeagueEvents.length, eventPage])

  const eventEndIndex = useMemo(() => {
    if (!activeLeagueEvents.length) return 0
    return Math.min(eventStartIndex + paginatedEvents.length - 1, activeLeagueEvents.length)
  }, [eventStartIndex, paginatedEvents.length, activeLeagueEvents.length])

  useEffect(() => {
    if (!activeLeagueEvents.length && eventPage !== 1) {
      setEventPage(1)
      return
    }
    if (eventPage > totalEventPages && totalEventPages > 0) {
      setEventPage(totalEventPages)
    }
  }, [activeLeagueEvents.length, eventPage, totalEventPages])

  const activeLeagueName = useMemo(() => {
    const activeLeague = leagues.find((league) => league.leagueId === activeLeagueId)
    return activeLeague ? activeLeague.leagueName : ''
  }, [leagues, activeLeagueId])

  const resolvedLastSavedAt = useMemo(() => {
    if (logs?.save_all) {
      return new Date(logs.save_all)
    }
    return lastSavedAt
  }, [logs?.save_all, lastSavedAt])

  const handleToggleSelection = (eventId, payload) => {
    setSelectionState((prev) => {
      const current = prev[eventId]
      const next = { ...current, ...payload }
      if (!next.single && !next.parlay) {
        const updated = { ...prev }
        delete updated[eventId]
        return updated
      }
      return {
        ...prev,
        [eventId]: {
          ...next,
          id: eventId,
        },
      }
    })
  }

  const handleRemoveSelection = (eventId) => {
    setSelectionState((prev) => {
      const updated = { ...prev }
      delete updated[eventId]
      return updated
    })
  }

  const handleReset = () => {
    resetFilters()
    setSelectionState({})
    setLastSavedAt(null)
    setActiveLeagueId('')
    setLeaguePage(1)
    setEventPage(1)
  }

  const handleSave = async () => {
    // console.table(
    //   selectedEvents.map((event) => ({
    //     event: `${event.homeTeam} vs ${event.awayTeam}`,
    //     league: event.leagueName,
    //     single: event.single ? 'Yes' : 'No',
    //     parlay: event.parlay ? 'Yes' : 'No',
    //     fivePercent: event.fivePercent ? 'Yes' : 'No',
    //     eightPercent: event.eightPercent ? 'Yes' : 'No',
    //   })),
    // )

    await saveLog({ eventId: "0", action: "SAVE_ALL", title: "League Selection" });

    if(!selectedEvents.length){
      dispatch(setToastMessage({ type: "error", message: "No events selected to save." }));
      return;
    }

    try {
      setIsLoading(true);

      setLastSavedAt(new Date());

      const finalData = Object.values(
        selectedEvents.reduce((acc, data) => {
          if (!acc[data.leagueId]) {
            acc[data.leagueId] = {
              leagueId: Number(data.leagueId),
              leagueName: data.leagueName,
              eventDetails: [],
            };
          }
          acc[data.leagueId].eventDetails.push({
            rapidEventId: data.rapidEventId,
            homeTeam: data.homeTeam,
            homeTeamId: data.homeTeamId,
            awayTeamId: data.awayTeamId,
            awayTeam: data.awayTeam,
            eventDate: data.eventDate,
            eventTime: data.eventTime,
            isSingle: Boolean(data.single),
            isMix: Boolean(data.parlay),
            isFivePercent: Boolean(data.single && data.fivePercent)
          });
          return acc;
        }, {})
      );

      //console.log("finalData ", finalData);

      const res = await leagueSelectionService.saveLeagueToBuffer(finalData);
      //console.log("Save response ", res);

      if( res?.status === 200 ){  
        await saveLog({ eventId: "0", action: "SAVE_ALL", title: "League Selection" });
        dispatch(setToastMessage({ type: "success", message: "League selections saved successfully." }));
      }
      else{
         dispatch(setToastMessage({ type: "error", message: res?.data?.message || "Failed to save league selections. Please try again." }));
      }
    }
    catch (error) {
      console.error("Failed to save league selections ", error);
      dispatch(setToastMessage({ type: "error", message: "Failed to save league selections. Please try again." }));
    }
    finally {
      setIsLoading(false);
    } 
  }

  const handleSelectLeague = (leagueId) => {
    setActiveLeagueId(leagueId)
  }

  const handleToggleAllByType = (type, checked) => {
    setSelectionState((prev) => {
      const updated = { ...prev }
      activeLeagueEvents.forEach((event) => {
        const current = updated[event.id] || { id: event.id }
        const next = {
          ...current,
          [type]: checked,
        }
        if (type === 'single') {
          next.fivePercent = checked ? current.fivePercent ?? false : false
          next.eightPercent = checked ? current.eightPercent ?? false : false
        }
        if (!next.single && !next.parlay) {
          delete updated[event.id]
        } else {
          updated[event.id] = next
        }
      })
      return { ...updated }
    })
  }

  return (
    <div className="flex flex-col gap-4">
      {isLoading && <LoadingSpinner size={16} color="customAccent" />}
      <SelectionToolbar
        selectedCount={selectedCount}
        onSave={handleSave}
        onReset={handleReset}
        lastSavedAt={resolvedLastSavedAt}
        filtersVisible={filtersVisible}
        referenceVisible={referenceVisible}
        onToggleFilters={() => setFiltersVisible((current) => !current)}
        onToggleReference={() => setReferenceVisible((current) => !current)}
      />
      {filtersVisible ? (
        <FiltersPanel
          filters={filters}
          onFiltersChange={updateFilters}
          onReset={resetFilters}
          events={events}
        />
      ) : null}
      {fetchError ? (
        <div className="rounded-2xl border border-customOrange/40 bg-customOrange/10 px-4 py-3 text-sm text-customOrange dark:border-customOrange/60 dark:bg-customOrange/20 dark:text-customHeading">
          {fetchError}
          <button
            type="button"
            onClick={loadLeagueEvents}
            className="ml-3 rounded-lg border border-customOrange/40 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-customOrange transition hover:bg-customOrange/20 focus:outline-none focus:ring-2 focus:ring-customOrange/40 dark:border-customHeading dark:text-customHeading"
          >
            Retry
          </button>
        </div>
      ) : null}
      {isLoading ? (
        <div className="flex min-h-[360px] items-center justify-center rounded-2xl border border-dashed border-customAccent/30 bg-white/80 text-sm font-semibold text-customSecondaryText shadow-inner dark:border-customSurfaceMuted dark:bg-customSurface">
          <span className="flex items-center gap-3">
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-customAccent/60 border-t-transparent" />  
            Loading league data...
          </span>
        </div>
      ) : (
          <div className={`grid gap-1 xl:grid-cols-${referenceVisible ? '3' : '2'}`}>
            <div className="">
              <LeagueTable
                leagues={paginatedLeagues}
                activeLeagueId={activeLeagueId}
                onSelectLeague={handleSelectLeague}
                sortOption={leagueSortOption}
                onSortChange={setLeagueSortOption}
                searchTerm={leagueSearchTerm}
                onSearchChange={setLeagueSearchTerm}
                pagination={{
                  startIndex: leagueStartIndex,
                  endIndex: leagueEndIndex,
                  totalItems: searchableLeagues.length,
                  currentPage: totalLeaguePages ? leaguePage : 0,
                  totalPages: totalLeaguePages,
                  onPageChange: setLeaguePage,
                }}
              />
            </div>
            <div className=''>
              <LeagueEventsTable
                leagueName={activeLeagueName}
                events={paginatedEvents}
                totalEvents={activeLeagueEvents.length}
                selectionState={selectionState}
                allParlaySelected={allLeagueParlaySelected}
                allSingleSelected={allLeagueSingleSelected}
                allUpliftSelected={allUpliftSelected}
                onToggleSelection={handleToggleSelection}
                onToggleAll={handleToggleAllByType}
                pagination={{
                  startIndex: eventStartIndex,
                  endIndex: eventEndIndex,
                  totalItems: activeLeagueEvents.length,
                  currentPage: totalEventPages ? eventPage : 0,
                  totalPages: totalEventPages,
                  onPageChange: setEventPage,
                }}
              />
            </div>
            { referenceVisible &&
              <div className="">
                <IFramePanel />
              </div>
            }
          </div>
      )}
      <SelectedEventsPanel selectedEvents={selectedEvents} onRemoveSelection={handleRemoveSelection} isMobile={onlyWidth < 640} />
    </div>
  )
}

export default LeagueSelectionPage;

//grid gap-6 xl:grid-cols-3
